import dayjs from 'dayjs'
import { DATE_PARAM_FORMAT } from './constants'
import type { DailyCloseProductFormValues } from './filterToRequest'

export interface DailyCloseProductSearchQuery {
  shipperCode?: string
  closeDateFrom?: string
  closeDateTo?: string
  brandName?: string
  goodsCode?: string
  barcodes?: string[] | string
  logisticsServiceType?: string
}

export const toSearchQuery = (values: DailyCloseProductFormValues): DailyCloseProductSearchQuery => {
  const [from, to] = values.closeDateRange ?? []
  return {
    ...(values.shipperCode ? { shipperCode: values.shipperCode } : {}),
    ...(from ? { closeDateFrom: from.format(DATE_PARAM_FORMAT) } : {}),
    ...(to ? { closeDateTo: to.format(DATE_PARAM_FORMAT) } : {}),
    ...(values.brandName ? { brandName: values.brandName } : {}),
    ...(values.goodsCode ? { goodsCode: values.goodsCode } : {}),
    ...(values.barcodes?.length ? { barcodes: values.barcodes } : {}),
    ...(values.logisticsServiceType ? { logisticsServiceType: values.logisticsServiceType } : {}),
  }
}

export const hasDailyCloseProductSearchQuery = (query: Partial<DailyCloseProductSearchQuery>): boolean => {
  const hasBarcodes = Array.isArray(query.barcodes) ? query.barcodes.length > 0 : Boolean(query.barcodes)
  return Boolean(
    query.closeDateFrom || query.closeDateTo || query.shipperCode || query.brandName || query.goodsCode || hasBarcodes || query.logisticsServiceType,
  )
}

const text = (value: unknown): string | undefined => (typeof value === 'string' && value ? value : undefined)

const restoreDate = (value: unknown) => {
  const raw = text(value)
  if (!raw || !/^\d{8}$/.test(raw)) return null
  const parsed = dayjs(`${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`)
  return parsed.isValid() && parsed.format(DATE_PARAM_FORMAT) === raw ? parsed : null
}

export const fromSearchQuery = (query: Partial<DailyCloseProductSearchQuery>, defaults: DailyCloseProductFormValues): DailyCloseProductFormValues => {
  const from = restoreDate(query.closeDateFrom)
  const to = restoreDate(query.closeDateTo)
  let rawBarcodes: unknown[] = []
  if (Array.isArray(query.barcodes)) rawBarcodes = query.barcodes
  else if (query.barcodes != null) rawBarcodes = [query.barcodes]
  const barcodes = rawBarcodes.filter((value): value is string => typeof value === 'string' && Boolean(value))
  const hasSavedSearch = hasDailyCloseProductSearchQuery(query)

  return {
    shipperCode: text(query.shipperCode) ?? (hasSavedSearch ? undefined : defaults.shipperCode),
    closeDateRange: from && to ? [from, to] : defaults.closeDateRange,
    brandName: text(query.brandName),
    goodsCode: text(query.goodsCode),
    barcodes: barcodes.length ? barcodes : undefined,
    logisticsServiceType: text(query.logisticsServiceType),
  }
}
